// Defines the basic dimensional templates for various social media formats.
// This allows for easy reuse of common sizes like Square, Portrait, etc.
const socialFormatTemplates = {
  Square: {
    width: 1080,
    height: 1080,
    importName: 'square',
    name: 'Post Square',
  },
  Portrait: {
    width: 1080,
    height: 1350,
    importName: '_1080x1350',
    name: 'Post Portrait',
  },
  Story: {
    width: 1080,
    height: 1920,
    importName: 'story',
    name: 'Story',
  },
  Landscape: {
    width: 1200,
    height: 627,
    importName: 'landscape',
    name: 'Post Landscape',
  },
  LandscapeX: {
    width: 1920,
    height: 1080,
    importName: 'landscape',
    name: 'Post Landscape',
  },
  LandscapeYoutube: {
    width: 1280,
    height: 720,
    importName: 'landscape',
    name: '',
  },
};

// Configuration for each social media platform.
// Each object specifies the platform's name and the formats it supports,
// including unique overrides for names, IDs, and presetComponent components.
const socialPlatforms = [
  {
    name: 'Instagram',
    platform: 'instagram',
    formats: [
      { format: 'Square', media: ['image', 'video'] },
      { format: 'Portrait', media: ['image', 'video'] },
      { format: 'Story', media: ['image', 'video'] },
      {
        format: 'Story',
        name: 'Reels',
        idName: 'reels',
        media: ['video'],
        presetName: 'Reels',
      },
    ],
  },
  {
    name: 'Facebook',
    platform: 'facebook',
    formats: [
      { format: 'Square', media: ['image', 'video'] },
      { format: 'Portrait', media: ['image', 'video'] },
      { format: 'Story', media: ['image', 'video'] },
    ],
  },
  {
    name: 'LinkedIn',
    platform: 'linkedin',
    formats: [
      { format: 'Square', media: ['image', 'video'] },
      { format: 'Landscape', media: ['image', 'video'] },
    ],
  },
  {
    name: 'X',
    platform: 'x',
    formats: [{ format: 'LandscapeX', media: ['image', 'video'] }],
  },
  {
    name: 'TikTok',
    platform: 'tiktok',
    formats: [
      {
        format: 'Story',
        name: 'TikTok',
        idName: 'tiktok',
        media: ['video'],
        presetName: 'TikTok',
      },
    ],
  },
  {
    name: 'Youtube',
    platform: 'youtube',
    formats: [
      {
        format: 'LandscapeYoutube',
        name: 'Youtube',
        idName: 'youtube',
        media: ['video'],
        presetName: 'Youtube',
      },
    ],
  },
];

// Programmatically generates the full list of social media presets.
// It iterates through each platform and format, creating a preset for each
// specified media type (image or video) based on the templates.
// eslint-disable-next-line max-len
const generatedSocialPresets = socialPlatforms.flatMap((platform) => platform.formats.flatMap((formatInfo) => {
  // Get the base template for the current format
  const template = socialFormatTemplates[formatInfo.format];

  // Create a preset for each media type (e.g., image, video)
  return formatInfo.media.map((mediaType) => {
    const isVideo = mediaType === 'video';
    const videoLabel = isVideo ? 'Video ' : '';

    // Determine the preset's name, allowing for overrides.
    const customName = formatInfo.name || template.name;
    // Handle specific edge cases like Facebook's "Post Vertical".
    // const finalName = customName === 'Post Portrait' && platform.name === 'Facebook'
    // ? 'Post Vertical'
    // : customName;
    const finalName = customName;

    // Construct the presetComponent component name.
    const presetBase = formatInfo.presetName
      || `${platform.name}${finalName.replace(/\s/g, '')}`;

    const presetComponent = isVideo
      ? formatInfo.presetName || `${presetBase}Video`
      : presetBase;

    // Construct the unique ID for the preset.
    const id = formatInfo.idName
      || `${platform.name.toLowerCase()}-${videoLabel.toLowerCase()}${finalName
        .toLowerCase()
        .replace(/\s/g, '-')}`
        .replace('post-', '')
        .replace('facebook-', '');

    // Return the final preset object.
    return {
      width: template.width,
      height: template.height,
      label: `${platform.name} ${videoLabel}${finalName}`.trim(),
      id,
      importName: template.importName,
      presetComponent,
      category: 'social',
      platform: platform.platform,
      type: mediaType,
    };
  });
}));

// Manually defined presets for print products, which have unique properties
// and do not fit the programmatic generation model for social media.
const printPresets = [
  {
    width: 2700,
    height: 1800,
    label: 'Postcard Standard',
    id: 'postcard-standard',
    importName: 'postcardStandard',
    presetComponent: 'PostcardStandard',
    category: 'print',
    platform: 'postcard',
    type: 'image',
  },
  {
    width: 3300,
    height: 1800,
    label: 'Postcard Jumbo',
    id: 'postcard-jumbo',
    importName: 'postcardJumbo',
    presetComponent: 'PostcardJumbo',
    category: 'print',
    platform: 'postcard',
    type: 'image',
  },
  {
    width: 2550,
    height: 3300,
    label: 'Flyer Portrait Single-Sided',
    id: 'flyer-portrait-single',
    importName: 'flyer',
    presetComponent: 'FlyerPortraitSingleSided',
    category: 'print',
    platform: 'flyer',
    type: 'image',
  },
  {
    width: 3300,
    height: 2550,
    label: 'Flyer Landscape Single-Sided',
    id: 'flyer-landscape-single',
    importName: 'flyer',
    presetComponent: 'FlyerLandscapeSingleSided',
    category: 'print',
    platform: 'flyer',
    type: 'image',
  },
  {
    width: 2550,
    height: 3300,
    label: 'Flyer Portrait Double-Sided',
    id: 'flyer-portrait-double',
    importName: 'flyerDouble',
    presetComponent: 'FlyerPortraitDoubleSided',
    category: 'print',
    platform: 'flyer',
    type: 'image',
  },
  {
    width: 3300,
    height: 2550,
    label: 'Flyer Landscape Double-Sided',
    id: 'flyer-landscape-double',
    importName: 'flyerDouble',
    presetComponent: 'FlyerLandscapeDoubleSided',
    category: 'print',
    platform: 'flyer',
    type: 'image',
  },
];

// Manually defined presets for email templates
const emailPresets = [
  {
    width: 600,
    height: 1500,
    label: 'Email Template',
    id: 'email-template',
    importName: 'email',
    presetComponent: 'EmailTemplate',
    category: 'email',
    platform: 'email',
    type: 'html',
  },
];

// Combine the generated social presets with the manual print and email presets
// to create the final, comprehensive list for the application.
export const presetDefinitions = [...generatedSocialPresets, ...printPresets, ...emailPresets];

// Custom preset definition for user-defined dimensions
// This allows users to create projects with custom width, height, and units
// with validation limits of 6500px or 24 inches maximum
export const customPreset = {
	id: 'custom',
	label: 'Custom Size',
	category: 'custom',
	importName: '_custom',
	width: 1600, // Default width
	height: 1080, // Default height
	unit: 'px', // Default unit
	maxPx: 6000, // Maximum pixels allowed
	maxInches: 24, // Maximum inches allowed
	availableUnits: ['px', 'in'], // Available unit options
	presetComponent: null, // No presetComponent needed for custom
};

export default presetDefinitions;
